/**
 * What Was Posted Table Component
 * 
 * Evidence-driven Proof of Posting table with:
 * - Server-side pagination
 * - Server-side search
 * - Server-side sorting
 * - Cascading filters (Store, Campaign, Operator)
 */

import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { DataTable } from '@/components/tables/data-table';
import { createWhatWasPostedColumns, type WhatWasPostedRow } from '@/components/tables/what-was-posted-columns';
import { WhatWasPostedModal } from './WhatWasPostedModal';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { RefreshCw, Filter, X, Image as ImageIcon } from 'lucide-react';
import { http } from '@/lib/http';
import { toast } from '@/hooks/use-toast';

interface WhatWasPostedResponse {
  data: WhatWasPostedRow[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

interface FilterOptions {
  stores: Array<{ id: number; name: string }>;
  campaigns: Array<{ id: number; name: string }>;
  operators: Array<{ id: number; name: string; username: string }>;
}

interface WhatWasPostedTableProps {
  retailerId: number;
  cycleId: number;
  operators: Array<{ id: number; name: string; username: string }>;
}

export function WhatWasPostedTable({ 
  retailerId, 
  cycleId, 
  operators 
}: WhatWasPostedTableProps) {
  // Filter state
  const [selectedOperator, setSelectedOperator] = useState<string>('all');
  const [selectedStore, setSelectedStore] = useState<string>('all');
  const [selectedCampaign, setSelectedCampaign] = useState<string>('all');
  const [selectedTaskType, setSelectedTaskType] = useState<string>('all');
  
  // Table state
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('postedAt');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  
  // Modal state
  const [selectedRow, setSelectedRow] = useState<WhatWasPostedRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState<string | null>(null);

  // Fetch filter options (stores/campaigns with evidence in this cycle)
  const { data: filterOptions, isLoading: filtersLoading } = useQuery<FilterOptions>({
    queryKey: ['/api/proof-of-posting/what-was-posted/filters', retailerId, cycleId],
    queryFn: async () => {
      const response = await http(
        `/api/proof-of-posting/what-was-posted/filters?retailerId=${retailerId}&cycleId=${cycleId}`
      );
      if (!response.ok) throw new Error('Failed to fetch filter options');
      return response.json();
    },
    enabled: !!retailerId && !!cycleId,
  });

  // Fetch evidence data with server-side pagination and filtering
  const { 
    data, 
    isLoading, 
    isFetching,
    refetch 
  } = useQuery<WhatWasPostedResponse>({
    queryKey: [
      '/api/proof-of-posting/what-was-posted',
      retailerId,
      cycleId,
      selectedOperator,
      selectedStore,
      selectedCampaign,
      selectedTaskType,
      search,
      sortBy,
      sortDirection,
      page,
      pageSize
    ],
    queryFn: async () => {
      const params = new URLSearchParams({
        retailerId: retailerId.toString(),
        cycleId: cycleId.toString(),
        page: page.toString(),
        pageSize: pageSize.toString(),
        sortBy,
        sortDirection,
      });

      if (selectedOperator !== 'all') params.set('operatorId', selectedOperator);
      if (selectedStore !== 'all') params.set('storeId', selectedStore);
      if (selectedCampaign !== 'all') params.set('campaignId', selectedCampaign);
      if (selectedTaskType !== 'all') params.set('taskType', selectedTaskType);
      if (search) params.set('search', search);

      const response = await http(`/api/proof-of-posting/what-was-posted?${params}`);
      if (!response.ok) throw new Error('Failed to fetch evidence data');
      return response.json();
    },
    enabled: !!retailerId && !!cycleId,
  });

  // Handlers
  const handleViewDetails = useCallback((row: WhatWasPostedRow) => {
    setSelectedRow(row);
    setModalOpen(true);
  }, []);

  const handleImageClick = useCallback((url: string) => {
    setLightboxImage(url);
  }, []);

  const handleClearFilters = useCallback(() => {
    setSelectedOperator('all');
    setSelectedStore('all');
    setSelectedCampaign('all');
    setSelectedTaskType('all');
    setSearch('');
    setPage(1);
  }, []);

  const hasActiveFilters = selectedOperator !== 'all' || 
    selectedStore !== 'all' || 
    selectedCampaign !== 'all' ||
    selectedTaskType !== 'all' ||
    search !== '';

  // Create columns with handlers
  const columns = useMemo(() => 
    createWhatWasPostedColumns({
      onViewDetails: handleViewDetails,
      onImageClick: handleImageClick
    }), 
    [handleViewDetails, handleImageClick]
  );

  // Summary stats
  const summary = useMemo(() => {
    if (!data?.data) return { complete: 0, incomplete: 0, pending: 0, total: 0 };
    return {
      complete: data.data.filter(r => r.status === 'complete').length,
      incomplete: data.data.filter(r => r.status === 'incomplete').length,
      pending: data.data.filter(r => r.status === 'pending').length,
      total: data.totalCount
    };
  }, [data]);

  return (
    <div className="space-y-4">
      {/* Filters Card */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg flex items-center gap-2">
              <Filter className="h-5 w-5" />
              Filters
            </CardTitle>
            <div className="flex items-center gap-2">
              {hasActiveFilters && (
                <Button 
                  variant="outline" 
                  size="sm" 
                  onClick={handleClearFilters}
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear Filters
                </Button>
              )}
              <Button 
                variant="outline" 
                size="sm" 
                onClick={() => refetch()}
                disabled={isFetching}
              >
                <RefreshCw className={`h-4 w-4 mr-1 ${isFetching ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Type Filter (Posting/Removal) */}
            <div className="space-y-2">
              <Label>Type</Label>
              <Select 
                value={selectedTaskType} 
                onValueChange={(v) => {
                  setSelectedTaskType(v);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="POSTING">Posting</SelectItem>
                  <SelectItem value="REMOVAL">Removal</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Operator Filter */}
            <div className="space-y-2">
              <Label>Operator</Label>
              <Select 
                value={selectedOperator} 
                onValueChange={(v) => {
                  setSelectedOperator(v);
                  setPage(1);
                }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Operators" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Operators</SelectItem>
                  {(filterOptions?.operators || operators).map(op => (
                    <SelectItem key={op.id} value={op.id.toString()}>
                      {op.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Store Filter */}
            <div className="space-y-2">
              <Label>Store</Label>
              <Select 
                value={selectedStore} 
                onValueChange={(v) => {
                  setSelectedStore(v);
                  setPage(1);
                }}
                disabled={filtersLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Stores" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stores</SelectItem>
                  {filterOptions?.stores?.map(store => (
                    <SelectItem key={store.id} value={store.id.toString()}>
                      {store.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Campaign Filter */}
            <div className="space-y-2">
              <Label>Campaign</Label>
              <Select 
                value={selectedCampaign} 
                onValueChange={(v) => {
                  setSelectedCampaign(v);
                  setPage(1);
                }}
                disabled={filtersLoading}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All Campaigns" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Campaigns</SelectItem>
                  {filterOptions?.campaigns?.map(campaign => (
                    <SelectItem key={campaign.id} value={campaign.id.toString()}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Summary Stats */}
            <div className="space-y-2">
              <Label>Summary</Label>
              <div className="flex items-center gap-2 h-10">
                <Badge variant="outline" className="bg-green-50 text-green-700">
                  {summary.complete} Complete
                </Badge>
                <Badge variant="outline" className="bg-orange-50 text-orange-700">
                  {summary.incomplete} Incomplete
                </Badge>
                <Badge variant="secondary">
                  {summary.total} Total
                </Badge>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <ImageIcon className="h-5 w-5" />
            Posted Evidence
            {data?.totalCount !== undefined && (
              <Badge variant="secondary" className="ml-2">
                {data.totalCount} records
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={data?.data || []}
            isLoading={isLoading}
            searchKey="storeName"
            searchPlaceholder="Search stores, campaigns, operators..."
            showColumnToggle
            serverSearch={{
              value: search,
              onChange: (value) => {
                setSearch(value);
                setPage(1);
              },
              placeholder: "Search stores, campaigns, operators..."
            }}
            serverPagination={{
              currentPage: page,
              totalPages: data?.totalPages || 1,
              totalCount: data?.totalCount || 0,
              pageSize,
              onPageChange: setPage,
              onPageSizeChange: setPageSize,
              pageSizeOptions: [10, 25, 50, 100]
            }}
            onRowClick={handleViewDetails}
          />
        </CardContent>
      </Card>

      {/* Details Modal */}
      <WhatWasPostedModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedRow(null);
        }}
        row={selectedRow}
        cycleId={cycleId}
        onSuccess={() => refetch()}
      />

      {/* Lightbox for single image */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center cursor-pointer"
          onClick={() => setLightboxImage(null)}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setLightboxImage(null)}
          >
            <X className="h-6 w-6" />
          </Button>
          <img
            src={lightboxImage}
            alt="Evidence"
            className="max-w-[90vw] max-h-[90vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}

export default WhatWasPostedTable;

